// [2026-09-04] ADDED — the presentational vocabulary of the revamped customer screens.
//
// WHY NOT REUSE src/ui.jsx's Card/Tile/Modal
// ------------------------------------------
// Because those are SHARED. src/index.js re-exports all of ui.jsx (`export * from './ui.jsx'`) and
// proctoring-frontend-admin renders `Card` and `Tile` on nine staff screens. Restyling them to the
// Figma would repaint that repository's UI without anyone touching it — the break appears at ITS
// build, not this one's.
//
// So the customer screens get their own primitives here and src/ui.jsx is left EXACTLY as it is.
// Nothing was deleted or renamed; `Card`, `Tile`, `Modal`, `Empty` and `Notice` still exist there
// and still export from the package. This file is purely additive.
//
// The `Pf` prefix is not decoration either — it makes a mixed import in a half-migrated screen
// unambiguous at the call site (`<PfCard>` vs `<Card>`) instead of relying on which import won.

import { useEffect } from 'react'

/** A panel: title, caption, optional right-hand controls. Mirrors ui.jsx's Card API deliberately
 *  (title / subtitle / actions / children), so converting a screen is a rename and not a rewrite. */
export function PfCard({ title, subtitle, actions, children, className = '', style }) {
  return (
    <section className={`pf-card ${className}`} style={style}>
      {(title || actions) && (
        <div className="pf-card-head">
          <div>
            {title && <h3>{title}</h3>}
            {subtitle && <p>{subtitle}</p>}
          </div>
          {actions}
        </div>
      )}
      {children}
    </section>
  )
}

/** A KPI tile.
 *
 *  `note` is the small teal aside the Figma puts beside a figure ("12 sessions live now"); `sub` is
 *  the muted line under it. Both are optional and neither is ever the only place a number appears —
 *  the tone prop colours the figure but the LABEL always says what it is, so a red number is never
 *  the only signal that something is wrong.
 *
 *  [2026-09-25] ADDED — `iconTone` ('blue' | 'teal' | 'red' | 'amber' | 'green'): puts the icon on
 *  the Figma's coloured rounded-square chip. OPTIONAL and additive — a tile that does not pass it
 *  renders exactly as before (grey glyph, no chip), so every existing caller, including the admin
 *  app that compiles against this file, is unchanged. The colour is decoration beside a text label,
 *  never the meaning (see the paragraph above). */
// [2026-09-25] CHANGED — `iconTone` added to the props; the icon span gets the chip classes from it.
// OLD: export function PfTile({ label, value, unit, note, sub, tone, icon: Icon }) {
export function PfTile({ label, value, unit, note, sub, tone, icon: Icon, iconTone }) {
  return (
    <div className="pf-tile tile">
      <div className="pf-tile-top">
        <span className="pf-tile-label">{label}</span>
        {/* OLD: {Icon && <span className="pf-tile-icon"><Icon /></span>} */}
        {Icon && (
          <span className={iconTone ? `pf-tile-icon chip tone-${iconTone}` : 'pf-tile-icon'}>
            <Icon />
          </span>
        )}
      </div>
      <div className="pf-tile-value" style={tone ? { color: tone } : undefined}>
        {value}
        {unit && <span className="unit">{unit}</span>}
        {note && <span className="pf-tile-note">{note}</span>}
      </div>
      {sub && <div className="pf-tile-sub">{sub}</div>}
    </div>
  )
}

export function PfEmpty({ title, hint }) {
  return (
    <div className="pf-empty">
      <strong>{title}</strong>
      {hint && <span>{hint}</span>}
    </div>
  )
}

export function PfNotice({ kind = 'info', children }) {
  if (!children) return null
  return <div className={`pf-notice ${kind}`}>{children}</div>
}

/** A count with a tint behind it, for the endpoint table's success / error columns.
 *  The NUMBER is the content; the tint is redundant encoding, never the message. */
export function PfPill({ tone = 'neutral', children }) {
  return <span className={`pf-pill ${tone}`}>{children}</span>
}

/** A status word. Always the word — see the badge rule in theme.css and the reason behind it. */
export function PfChip({ tone = 'idle', children }) {
  return <span className={`pf-chip ${tone}`}>{children}</span>
}

/** A modal.
 *
 *  Escape closes, and a click on the backdrop (but not inside the panel) closes. Both were already
 *  true of ui.jsx's Modal and are re-implemented rather than inherited only because the CHROME
 *  differs; the behaviour is deliberately identical, so a user who learned one knows the other.
 *
 *  NOTE the classes here are NOT under `.pf-app` — a modal renders in place, inside the shell, so
 *  it inherits the scope. The stylesheet defines `.pf-modal*` unscoped for exactly that reason:
 *  a portal (if one is ever added) would render outside `.pf-app` and must still be styled. */
export function PfModal({ title, subtitle, onClose, children, wide }) {
  useEffect(() => {
    const onKey = (event) => { if (event.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  return (
    <div className="pf-modal-backdrop"
         onClick={(event) => { if (event.target === event.currentTarget) onClose() }}>
      <div className={`pf-modal${wide ? ' wide' : ''}`} role="dialog" aria-modal="true">
        <div className="pf-modal-head">
          <div>
            <h2>{title}</h2>
            {subtitle && <p className="pf-modal-sub">{subtitle}</p>}
          </div>
          <button className="pf-modal-close" onClick={onClose} aria-label="Close">×</button>
        </div>
        {children}
      </div>
    </div>
  )
}

/** The skeleton, restyled for the new page plane. ui.jsx's `.skeleton` is tuned to theme.css's warm
 *  greys (#f0efec on #f7f7f5) and looks like a rendering fault on the cooler #f5f7fa used here. */
// [2026-09-27] CHANGED — a card that is loading now shows the InCruiter SPINNER, not a grey block.
// Asked for by Rujal: "I want a spinner like other applications … currently I am seeing a square
// loading thing". Same size as before, so nothing on the page moves when the data arrives. Every
// caller (17 uses, all in views/platform) gets it from here; the admin app does not use PfSkeleton.
// OLD:
// export function PfSkeleton({ height = 80, style }) {
//   return (
//     <div className="skeleton" style={{
//       height,
//       borderRadius: 10,
//       background: 'linear-gradient(90deg, #eef1f6 25%, #f7f9fc 50%, #eef1f6 75%)',
//       backgroundSize: '200% 100%',
//       ...style,
//     }} />
//   )
// }
export function PfSkeleton({ height = 80, style }) {
  return <PfSpinner height={height} style={style} />
}

// [2026-09-27] ADDED — the loader every other InCruiter app shows: frontend-account and
// frontend-incbot `<Loader />` (`<div class="wrapper"><div class="loader">`, CommonComponent/Loader.jsx
// and Common/Loader.jsx). Two forms, as they use it:
//   · PfPageSpinner — the whole page is loading: a light grey overlay over the screen with the
//     spinner in the middle (their plain `.wrapper`, _global.scss:1472-1507).
//   · PfSpinner — ONE card is loading: the same spinner in the card's space, no overlay (their
//     `.chart-container .wrapper`, pages/_intro-page.scss:223-230).
// Styles: platform-theme.css `.pf-loader*`. role="status" so a screen reader announces it.
export function PfSpinner({ height = 80, style }) {
  return (
    <div className="pf-loader-inline" role="status" aria-label="Loading" style={{ height, ...style }}>
      <div className="pf-loader" />
    </div>
  )
}

export function PfPageSpinner() {
  return (
    <div className="pf-loader-page" role="status" aria-label="Loading">
      <div className="pf-loader" />
    </div>
  )
}
